// Secure authentication system for HR admin panel
// Uses server-side sessions with HttpOnly cookies and CSRF protection
// No more localStorage for security tokens

// Determine API URL similar to apiService.js
const getApiBaseUrl = () => {
  const isDevelopment = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1'
  
  return isDevelopment ? 'http://localhost:3001/api' : '/api'
}

const API_BASE_URL = getApiBaseUrl()

// Store CSRF token in memory (not localStorage for security)
let csrfToken = null

// SECURITY MIGRATION: Clear old localStorage tokens on page load
if (typeof window !== 'undefined') {
  // Clear any old localStorage authentication data
  const oldKeys = ['hr_admin_session', 'authToken', 'adminToken', 'hr_audit_logs'];
  oldKeys.forEach(key => {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
      console.log(`🔒 Cleared old auth data: ${key}`);
    }
  });
  
  // Clear all sessionStorage as well
  if (sessionStorage.length > 0) {
    sessionStorage.clear();
    console.log(`🔒 Cleared sessionStorage`);
  }
}

/**
 * Login with password verification and secure session creation
 */
export const login = async (password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies in request
      body: JSON.stringify({ password })
    });

    const result = await response.json();

    if (result.success) {
      // Store CSRF token in memory for secure requests
      csrfToken = result.csrfToken;
      
      return { 
        success: true, 
        message: result.message,
        user: result.user,
        expiresAt: result.expiresAt
      };
    } else {
      return { success: false, message: result.message };
    }
  } catch (error) {
    console.error('Login error:', error);
    return { success: false, message: "Ошибка соединения с сервером" };
  }
};

/**
 * Check authentication status using server-side session
 */
export const isAuthenticated = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies
    });

    const result = await response.json();

    if (result.success) {
      // Update CSRF token if provided
      if (result.csrfToken) {
        csrfToken = result.csrfToken;
      }
      return true;
    } else {
      csrfToken = null;
      return false;
    }
  } catch (error) {
    // 401 - это нормальное поведение для неавторизованных пользователей
    // Логируем только реальные сетевые ошибки
    if (error.message && !error.message.includes('401')) {
      console.error('Authentication check error:', error);
    }
    csrfToken = null;
    return false;
  }
};

/**
 * Get current session information (no localStorage needed)
 */
export const getSession = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    const result = await response.json();

    if (result.success) {
      if (result.csrfToken) {
        csrfToken = result.csrfToken;
      }
      
      return {
        user: result.user,
        expiresAt: result.expiresAt,
        authenticated: true
      };
    }
    
    return null;
  } catch (error) {
    console.error('Session info error:', error);
    return null;
  }
};

/**
 * Logout and destroy server-side session
 */
export const logout = async () => {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken && { 'X-CSRF-Token': csrfToken })
      },
      credentials: 'include',
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Clear in-memory CSRF token
    csrfToken = null;
  }
};

/**
 * Extend session through server
 */
export const extendSession = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/extend`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken && { 'X-CSRF-Token': csrfToken })
      },
      credentials: 'include',
    });

    const result = await response.json();

    if (result.success) {
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Session extension error:', error);
    return false;
  }
};

/**
 * Get time until session expiry (requires server call)
 */
export const getTimeToExpiry = async () => {
  try {
    const session = await getSession();
    if (!session || !session.expiresAt) return 0;
    
    // Убеждаемся, что expiresAt - это число (может прийти как строка)
    const expiresAt = typeof session.expiresAt === 'string' 
      ? parseInt(session.expiresAt, 10) 
      : session.expiresAt;
    
    // Проверяем, что получили валидное число
    if (isNaN(expiresAt) || expiresAt <= 0) {
      console.warn('Invalid expiresAt value:', session.expiresAt);
      return 0;
    }
    
    const now = Date.now();
    const timeLeft = expiresAt - now;
    
    // Возвращаем время в минутах, минимум 0
    const minutesLeft = Math.max(0, Math.floor(timeLeft / (60 * 1000)));
    
    return minutesLeft;
  } catch (error) {
    console.error('Time to expiry error:', error);
    return 0;
  }
};

/**
 * Get current CSRF token for secure API calls
 */
export const getCSRFToken = () => {
  return csrfToken;
};

/**
 * Refresh CSRF token
 */
export const refreshCSRFToken = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/csrf-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    const result = await response.json();

    if (result.success) {
      csrfToken = result.csrfToken;
      return csrfToken;
    }
    
    return null;
  } catch (error) {
    console.error('CSRF token refresh error:', error);
    return null;
  }
};

/**
 * Make secure API call with CSRF protection
 */
export const secureApiCall = async (url, options = {}) => {
  const defaultOptions = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
      ...options.headers
    }
  };

  const mergedOptions = { ...defaultOptions, ...options };
  
  try {
    const response = await fetch(url, mergedOptions);
    
    // If CSRF error, try to refresh token and retry once
    if (response.status === 403) {
      const newToken = await refreshCSRFToken();
      if (newToken) {
        mergedOptions.headers['X-CSRF-Token'] = newToken;
        return await fetch(url, mergedOptions);
      }
    }
    
    return response;
  } catch (error) {
    console.error('Secure API call error:', error);
    throw error;
  }
}; 